import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { api } from "../../../services/erpApi";
import { LoadingScreen } from "../../../components/admin/EssUi";
import { useToast } from "../../../components/admin/ToastContext";
import CrmV2ControlPanel from "./crm-v2/CrmV2ControlPanel";
import CrmV2KanbanBoard from "./crm-v2/CrmV2KanbanBoard";
import CrmV2ListView from "./crm-v2/CrmV2ListView";
import CrmV2CalendarView from "./crm-v2/CrmV2CalendarView";
import CrmV2GraphView from "./crm-v2/CrmV2GraphView";
import CrmV2PivotView from "./crm-v2/CrmV2PivotView";
import CrmV2GenerateLeadsModal from "./crm-v2/CrmV2GenerateLeadsModal";
import CrmV2OpportunityFormView from "./crm-v2/CrmV2OpportunityFormView";
import CrmV2MailComposerModal from "./crm-v2/CrmV2MailComposerModal";
import CrmV2WhatsAppComposerModal from "./crm-v2/CrmV2SmsComposerModal";
import CrmV2ChatRoomMessageModal from "./crm-v2/CrmV2ChatRoomMessageModal";
import CrmV2QuotationPage from "./crm-v2/CrmV2QuotationPage";
import CrmV2ActivityView from "./crm-v2/CrmV2ActivityView";
import CrmV2GroupsModal from "./crm-v2/CrmV2GroupsModal";
import {
  KANBAN_PAGE_SIZE,
  LIST_PAGE_SIZE,
  norm,
  ODOO,
  parseListResponse,
} from "./crm-v2/crmV2Helpers";

const emptyCol = () => ({ leads: [], total: 0, page: 0, loading: false, loadingMore: false });
const ANALYTICS_VIEWS = new Set(["calendar", "graph", "pivot"]);
const CRM_V2_VIEW_STORAGE_KEY = "afs.crm-v2.active-view";
const CRM_V2_VIEWS = new Set(["kanban", "list", "calendar", "graph", "pivot"]);

const getSavedView = () => {
  if (typeof window === "undefined") return "kanban";
  try {
    const savedView = window.localStorage.getItem(CRM_V2_VIEW_STORAGE_KEY);
    return CRM_V2_VIEWS.has(savedView) ? savedView : "kanban";
  } catch {
    return "kanban";
  }
};

// Upload des fichiers locaux puis liaison au lead (workflow documenté ROUTES.md :
// POST /api/uploads -> POST /crm-leads/:id/attachments). Retourne [{id, file_path,
// file_name, mime_type}]. Lève une erreur → l'appelant doit annuler l'envoi.
const uploadAndLinkLeadFiles = async (leadId, files) => {
  const linked = [];
  for (const file of Array.isArray(files) ? files : []) {
    let uploaded;
    try {
      uploaded = await api.upload(file);
    } catch (e) {
      throw new Error(`Upload "${file.name}" échoué : ${e.message || e}`);
    }
    const filePath = uploaded?.file_path || uploaded?.data?.file_path;
    if (!filePath) throw new Error(`Upload "${file.name}" sans file_path`);
    const created = await api.crm.addAttachment(leadId, {
      name: file.name,
      file_name: file.name,
      file_path: filePath,
      mime_type: file.type || undefined,
      file_size: file.size || undefined,
    });
    const newId = created?.id ?? created?.data?.id;
    if (newId == null) throw new Error(`Pièce jointe "${file.name}" non liée au lead`);
    linked.push({ id: newId, file_path: filePath, file_name: file.name, mime_type: file.type || undefined });
  }
  return linked;
};

// Résolution côté front des variables {{ lead.* }} pour l'envoi DIRECT WhatsApp
// (POST /whatsapp-v2/test n'a pas le moteur de templates du bulk).
// Variables inconnues laissées telles quelles (visibles, pas de silent drop).
const resolveLeadVars = (text, lead) => {
  if (!text || !lead) return text || "";
  const money = (v) => (v == null || v === "" ? "" : `${Number(v).toLocaleString("en-US", { maximumFractionDigits: 0 })} FCFA`);
  const map = {
    "lead.name": lead.name || "",
    "lead.type": lead.type === "opportunity" ? "Opportunity" : "Lead",
    "lead.stage_name": lead.stage_name || "",
    "lead.partner_name": lead.partner_name || lead.company || "",
    "lead.company": lead.company || lead.partner_name || "",
    "lead.email_from": lead.email_from || "",
    "lead.phone": lead.phone || lead.mobile || "",
    "lead.mobile": lead.mobile || lead.phone || "",
    "lead.expected_revenue": money(lead.expected_revenue),
    "lead.probability": lead.probability != null ? `${lead.probability}%` : "",
    "lead.date_deadline": lead.date_deadline || "",
    "lead.user_name": lead.user_name || "",
    "lead.team_name": lead.team_name || "",
    "salesperson.name": lead.user_name || "",
    "salesperson.email": lead.user_email || "",
  };
  return String(text).replace(/\{\{\s*([\w.]+)\s*\}\}/g, (m, k) => (k in map ? map[k] : m));
};

// Normalise un numéro pour l'envoi direct (+237655230405, sans espaces).
const normalizePhone = (raw) => String(raw || "").trim().replace(/(?!^\+)[^\d]/g, "");

const AdminPanelCrmV2Page = () => {
  const toast = useToast();
  const permissions = useSelector((s) => s.auth?.permissions);
  const canManage = !!(permissions?.canManageCRM || permissions?.canManageCrm || permissions?.isAdmin);

  const [loaded, setLoaded] = useState(false);
  const [view, setView] = useState(getSavedView);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filters, setFilters] = useState({
    type: "all",
    stage_id: "all",
    team_id: "all",
    user_id: "all",
    priority: "all",
    group_id: "all",
    include_inactive: false,
  });

  const [stages, setStages] = useState([]);
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [foldedIds, setFoldedIds] = useState(() => new Set());

  const [columnState, setColumnState] = useState({});
  const [draggingLead, setDraggingLead] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  // Drag & drop des stages (colonnes) — même animation que les cartes mais sur l'entité colonne entière
  const [draggingStage, setDraggingStage] = useState(null);
  const [dragOverStageId, setDragOverStageId] = useState(null);

  const [listLeads, setListLeads] = useState([]);
  const [listTotal, setListTotal] = useState(0);
  const [listPage, setListPage] = useState(1);
  const [listLoading, setListLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);

  const [analyticsLeads, setAnalyticsLeads] = useState([]);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [activities, setActivities] = useState([]);
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const [activityTypes, setActivityTypes] = useState([]);
  const [activityLeads, setActivityLeads] = useState([]);

  const [inlineCreateStageId, setInlineCreateStageId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [addingStage, setAddingStage] = useState(false);
  const [showGenerateLeads, setShowGenerateLeads] = useState(false);

  // ─── Detail Form View (Odoo-like) ───
  const [detailLead, setDetailLead] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailListCache, setDetailListCache] = useState([]); // for pagination
  const [tagsCache, setTagsCache] = useState([]);
  const [groupsCache, setGroupsCache] = useState([]);
  const [showGroupsModal, setShowGroupsModal] = useState(false);
  const [partnersCache, setPartnersCache] = useState([]);
  const [sourcesCache, setSourcesCache] = useState([]);
  const [mediumsCache, setMediumsCache] = useState([]);
  const [campaignsCache, setCampaignsCache] = useState([]);
  const [lostReasonsCache, setLostReasonsCache] = useState([]);
  const [mailOpen, setMailOpen] = useState(false);
  const [mailTemplates, setMailTemplates] = useState([]);
  const [mailAttachments, setMailAttachments] = useState([]);
  const [waOpen, setWaOpen] = useState(false);
  const [waTemplates, setWaTemplates] = useState([]);
  const [chatRoomOpen, setChatRoomOpen] = useState(false);
  const [chatRoomTemplates, setChatRoomTemplates] = useState([]);
  const [quotationOpen, setQuotationOpen] = useState(false);
  const [quotationStartNew, setQuotationStartNew] = useState(false);

  // ─── Kanban card quick actions (V1 stability) ───
  const [kanbanAction, setKanbanAction] = useState(null); // {action, lead}
  const [kanbanBusy, setKanbanBusy] = useState(false);
  const [assignDraft, setAssignDraft] = useState({ user_id: "", team_id: "" });
  const [wonDraft, setWonDraft] = useState({ won_amount: "" });
  const [lostDraft, setLostDraft] = useState({ lost_reason_id: "" });

  const searchTimer = useRef(null);
  const filtersKey = useMemo(
    () =>
      JSON.stringify({
        search: debouncedSearch,
        type: filters.type,
        stage_id: filters.stage_id,
        team_id: filters.team_id,
        user_id: filters.user_id,
        priority: filters.priority,
        group_id: filters.group_id,
        include_inactive: filters.include_inactive,
      }),
    [debouncedSearch, filters]
  );

  const apiFilters = useMemo(() => {
    const f = {};
    if (debouncedSearch.trim()) f.search = debouncedSearch.trim();
    if (filters.type !== "all") f.type = filters.type;
    if (filters.stage_id !== "all") f.stage_id = filters.stage_id;
    if (filters.team_id !== "all") f.team_id = filters.team_id;
    if (filters.user_id !== "all") f.user_id = filters.user_id;
    if (filters.priority !== "all") f.priority = filters.priority;
    if (filters.group_id !== "all") f.group_id = filters.group_id;
    if (filters.include_inactive) f.include_inactive = true;
    return f;
  }, [debouncedSearch, filters]);

  // Conserve la dernière vue choisie même après un rechargement de la page.
  useEffect(() => {
    try {
      window.localStorage.setItem(CRM_V2_VIEW_STORAGE_KEY, view);
    } catch {
      // L'application reste utilisable si le navigateur bloque le stockage local.
    }
  }, [view]);

  useEffect(() => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(searchTimer.current);
  }, [search]);

  const userById = useMemo(() => {
    const m = new Map();
    users.forEach((u) => m.set(String(u.id), u));
    return m;
  }, [users]);

  const userNameOf = useCallback(
    (id) => {
      if (!id) return "";
      const u = userById.get(String(id));
      return u ? u.name || u.username || u.email || u.login || "" : "";
    },
    [userById]
  );

  const stageById = useMemo(() => {
    const m = new Map();
    stages.forEach((s) => m.set(String(s.id), s));
    return m;
  }, [stages]);

  const stageNameOf = useCallback(
    (id) => stageById.get(String(id))?.name || "",
    [stageById]
  );

  const activeStages = useMemo(
    () => stages.filter((s) => s.is_active !== false),
    [stages]
  );

  const loadReferentials = useCallback(async () => {
    const [s, t, u, tg, p, at, src, med, camp, lr, gr] = await Promise.all([
      api.crm.stages.list({ limit: 200 }).catch(() => []),
      api.crm.teams.list({ limit: 200 }).catch(() => []),
      api.users.list().catch(() => []),
      api.crm.tags.list({ limit: 200 }).catch(() => []),
      api.partners.list({ limit: 1000 }).catch(() => []),
      api.crm.activityTypes.list({ limit: 100 }).catch(() => []),
      api.crm.sources.list({ limit: 100 }).catch(() => []),
      api.crm.mediums.list({ limit: 100 }).catch(() => []),
      api.crm.campaigns.list({ limit: 100 }).catch(() => []),
      api.crm.lostReasons.list({ limit: 100 }).catch(() => []),
      api.crm.groups.list({ limit: 200 }).catch(() => []),
    ]);
    const sorted = norm(s || [])
      .slice()
      .sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));
    setStages(sorted);
    setTeams(norm(t || []));
    setUsers(norm(u || []));
    setTagsCache(norm(tg || []));
    setPartnersCache(norm(p || []));
    setActivityTypes(norm(at || []));
    setSourcesCache(norm(src || []));
    setMediumsCache(norm(med || []));
    setCampaignsCache(norm(camp || []));
    setLostReasonsCache(norm(lr || []));
    setGroupsCache(norm(gr || []));
    const folded = new Set();
    sorted.forEach((st) => {
      if (st.fold) folded.add(String(st.id));
    });
    setFoldedIds(folded);
  }, []);

  const loadColumn = useCallback(
    async (stageId, page = 1, append = false) => {
      setColumnState((prev) => ({
        ...prev,
        [stageId]: {
          ...(prev[stageId] || emptyCol()),
          loading: !append && page === 1,
          loadingMore: append || page > 1,
        },
      }));
      try {
        const res = await api.crm.list({
          ...apiFilters,
          stage_id: stageId,
          page,
          limit: KANBAN_PAGE_SIZE,
        });
        const { data, total } = parseListResponse(res);
        setColumnState((prev) => {
          const cur = prev[stageId] || emptyCol();
          const leads = append ? [...cur.leads, ...data] : data;
          return {
            ...prev,
            [stageId]: { leads, total, page, loading: false, loadingMore: false },
          };
        });
      } catch (e) {
        toast(e.message || "Load failed", "error");
        setColumnState((prev) => ({
          ...prev,
          [stageId]: { ...(prev[stageId] || emptyCol()), loading: false, loadingMore: false },
        }));
      }
    },
    [apiFilters, toast]
  );

  const loadAllColumns = useCallback(async () => {
    const cols = activeStages.filter((s) => {
      if (filters.stage_id !== "all" && String(filters.stage_id) !== String(s.id)) return false;
      return true;
    });
    await Promise.all(cols.map((s) => loadColumn(s.id, 1, false)));
  }, [activeStages, filters.stage_id, loadColumn]);

  const loadList = useCallback(
    async (page = 1) => {
      setListLoading(true);
      try {
        const res = await api.crm.list({
          ...apiFilters,
          page,
          limit: LIST_PAGE_SIZE,
          sort_by: "date_open",
          sort_dir: "desc",
        });
        const { data, total } = parseListResponse(res);
        setListLeads(data);
        setListTotal(total);
        setListPage(page);
        setSelectedIds([]);
      } catch (e) {
        toast(e.message || "Load failed", "error");
        setListLeads([]);
        setListTotal(0);
      }
      setListLoading(false);
    },
    [apiFilters, toast]
  );

  const loadAnalytics = useCallback(async () => {
    setAnalyticsLoading(true);
    try {
      const all = [];
      let pageNo = 1;
      const limit = 100;
      for (let guard = 0; guard < 15; guard++) {
        const res = await api.crm.list({ ...apiFilters, page: pageNo, limit });
        const { data, total } = parseListResponse(res);
        all.push(...data);
        if (data.length === 0 || all.length >= total || data.length < limit) break;
        pageNo += 1;
      }
      setAnalyticsLeads(all);
    } catch (e) {
      toast(e.message || "Load failed", "error");
      setAnalyticsLeads([]);
    }
    setAnalyticsLoading(false);
  }, [apiFilters, toast]);

  const loadActivities = useCallback(async () => {
    setActivitiesLoading(true);
    try {
      const [activityResponse, leadResponse] = await Promise.all([
        api.crm.activities.list({ limit: 500 }),
        api.crm.list({ ...apiFilters, limit: 500, page: 1 }),
      ]);
      const rows = norm(activityResponse);
      const leadRows = norm(leadResponse);
      setActivities(Array.isArray(rows) ? rows : []);
      setActivityLeads(Array.isArray(leadRows) ? leadRows : []);
    } catch (e) {
      toast(e.message || "Activities load failed", "error");
      setActivities([]);
    } finally {
      setActivitiesLoading(false);
    }
  }, [apiFilters, toast]);

  useEffect(() => {
    (async () => {
      await loadReferentials();
      setLoaded(true);
    })();
  }, [loadReferentials]);

  useEffect(() => {
    if (!loaded) return;
    if (view === "kanban") loadAllColumns();
    else if (view === "list") loadList(1);
    else if (view === "activity") loadActivities();
    else if (ANALYTICS_VIEWS.has(view)) loadAnalytics();
  }, [loaded, view, filtersKey, loadActivities, loadAllColumns, loadAnalytics, loadList]);

  const handleFilterChange = (patch) => {
    setFilters((prev) => ({ ...prev, ...patch }));
  };

  useEffect(() => {
    if (filters.group_id === "all" || groupsCache.length === 0) return;
    if (!groupsCache.some((g) => String(g.id) === String(filters.group_id))) {
      setFilters((prev) => ({ ...prev, group_id: "all" }));
    }
  }, [groupsCache, filters.group_id]);

  const handleToggleFold = (stageId, fold) => {
    setFoldedIds((prev) => {
      const next = new Set(prev);
      if (fold) next.add(String(stageId));
      else next.delete(String(stageId));
      return next;
    });
  };

  const handleLoadMore = (stageId) => {
    const st = columnState[stageId] || emptyCol();
    if (st.loadingMore || st.leads.length >= st.total) return;
    loadColumn(stageId, (st.page || 1) + 1, true);
  };

  const handleCardDragStart = (eOrLead, maybeLead) => {
    // Compatible avec CrmV2KanbanCard qui appelle onDragStart(e, lead) avec ghost custom
    // et l'ancien appel onDragStart(lead).
    const lead = maybeLead !== undefined ? maybeLead : eOrLead;
    if (lead && lead.id) {
      // Ne pas confliter avec un drag de colonne en cours
      if (draggingStage) return;
      setDraggingLead(lead);
    }
  };
  const handleCardDragEnd = () => {
    setDraggingLead(null);
    setDragOverId(null);
  };

  const handleDragOver = (stage) => {
    if (stage.is_won || stage.is_lost) return;
    if (draggingStage) return; // drag de colonne en cours → ignorer le drag de leads
    setDragOverId(stage.id);
  };

  const handleDragLeave = (stageId) => {
    setDragOverId((d) => (d === stageId ? null : d));
  };

  // --- Drag & drop des colonnes (stages) — sans loader visible ---
  const handleStageDragStart = (e, stage) => {
    if (draggingLead) return;
    if (!canManage) return;
    if (filters.stage_id !== "all") return; // une seule colonne visible → pas de réordonnancement
    if (stage?.is_won || stage?.is_lost) return;
    // Si le drag part d'une carte, on ignore (e.target.closest)
    try {
      const target = e?.target;
      if (target && target.closest && target.closest("[data-kanban-card]")) return;
    } catch {}
    setDraggingStage(stage);
    setDragOverStageId(null);
    if (e?.dataTransfer) {
      e.dataTransfer.effectAllowed = "move";
      try { e.dataTransfer.setData("text/plain", String(stage.id)); } catch {}
    }
  };
  const handleStageDragEnd = () => {
    setDraggingStage(null);
    setDragOverStageId(null);
  };
  const handleStageDragOver = (targetStage) => {
    if (!draggingStage) return;
    if (draggingLead) return;
    if (String(draggingStage.id) === String(targetStage.id)) return;
    if (targetStage?.is_won || targetStage?.is_lost) return;
    setDragOverStageId(targetStage.id);
  };
  const handleStageDragLeave = (stageId) => {
    setDragOverStageId((d) => (String(d) === String(stageId) ? null : d));
  };
  const handleStageDrop = async (targetStage) => {
    const dragged = draggingStage;
    setDragOverStageId(null);
    setDraggingStage(null);
    if (!dragged || !canManage) return;
    if (draggingLead) return;
    if (String(dragged.id) === String(targetStage.id)) return;
    if (targetStage?.is_won || targetStage?.is_lost) return;
    if (filters.stage_id !== "all") return;

    const prevStages = stages;
    const fromIdx = prevStages.findIndex((s) => String(s.id) === String(dragged.id));
    const toIdx = prevStages.findIndex((s) => String(s.id) === String(targetStage.id));
    if (fromIdx === -1 || toIdx === -1) return;

    const reordered = [...prevStages];
    const [moved] = reordered.splice(fromIdx, 1);
    reordered.splice(toIdx, 0, moved);

    // Optimistic UI — pas de loader, pas de flicker (même logique que CRM Settings → ORDER)
    setStages(reordered);

    try {
      // API officielle utilisée dans AdminPanelCrmSettingsPage.js:2615-2622
      // → api.crm.stages.reorder({ order: [...] }) avec l'ordre complet des IDs
      if (api.crm.stages.reorder) {
        await api.crm.stages.reorder({ order: reordered.map((s) => s.id) });
      } else {
        // Fallback si l'endpoint reorder n'existe pas : update séquence moyenne
        const newIdx = reordered.findIndex((s) => String(s.id) === String(dragged.id));
        let newSequence;
        if (reordered.length === 1) newSequence = 10;
        else if (newIdx === 0) newSequence = Number(reordered[1]?.sequence ?? 10) - 10;
        else if (newIdx === reordered.length - 1) newSequence = Number(reordered[newIdx - 1]?.sequence ?? 0) + 10;
        else {
          const prevSeq = Number(reordered[newIdx - 1]?.sequence ?? 0);
          const nextSeq = Number(reordered[newIdx + 1]?.sequence ?? prevSeq + 20);
          newSequence = (prevSeq + nextSeq) / 2;
          if (!Number.isFinite(newSequence) || Math.abs(nextSeq - prevSeq) < 0.5) newSequence = newIdx * 10;
        }
        await api.crm.stages.update(dragged.id, { sequence: newSequence });
        setStages((cur) => cur.map((s) => (String(s.id) === String(dragged.id) ? { ...s, sequence: newSequence } : s)));
      }
    } catch (e) {
      toast(e.message || "Stage move failed", "error");
      // Rollback silencieux — la colonne revient à sa position, sans loader
      setStages(prevStages);
    }
  };

  const handleDrop = async (targetStage) => {
    setDragOverId(null);
    if (!draggingLead || !canManage) return;
    if (targetStage.is_won || targetStage.is_lost) {
      toast("Terminal stage — use Won/Lost on the classic CRM", "error");
      setDraggingLead(null);
      return;
    }
    if (String(draggingLead.stage_id) === String(targetStage.id)) {
      setDraggingLead(null);
      return;
    }

    const lead = draggingLead;
    const fromId = lead.stage_id;
    setDraggingLead(null);

    setColumnState((prev) => {
      const next = { ...prev };
      const from = { ...(next[fromId] || emptyCol()) };
      from.leads = from.leads.filter((l) => l.id !== lead.id);
      from.total = Math.max(0, (from.total || 0) - 1);
      next[fromId] = from;

      const to = { ...(next[targetStage.id] || emptyCol()) };
      const moved = { ...lead, stage_id: targetStage.id };
      to.leads = [moved, ...to.leads.filter((l) => l.id !== lead.id)];
      to.total = (to.total || 0) + 1;
      next[targetStage.id] = to;
      return next;
    });

    try {
      await api.crm.update(lead.id, { stage_id: targetStage.id });
    } catch (e) {
      toast(e.message || "Move failed", "error");
      loadColumn(fromId, 1, false);
      loadColumn(targetStage.id, 1, false);
    }
  };

  const openInlineCreate = (stage) => {
    const target =
      stage ||
      activeStages.find((s) => !s.is_won && !s.is_lost && !foldedIds.has(String(s.id))) ||
      activeStages.find((s) => !s.is_won && !s.is_lost);
    if (!target) {
      toast("No open stage available", "error");
      return;
    }
    if (view !== "kanban") setView("kanban");
    setFoldedIds((prev) => {
      const next = new Set(prev);
      next.delete(String(target.id));
      return next;
    });
    setInlineCreateStageId(target.id);
  };

  const handleCreate = async (payload) => {
    setCreating(true);
    try {
      await api.crm.create(payload);
      toast("Opportunity created", "success");
      setInlineCreateStageId(null);
      if (payload.stage_id) await loadColumn(payload.stage_id, 1, false);
      else await loadAllColumns();
      if (view === "list") await loadList(listPage);
    } catch (e) {
      toast(e.message || "Create failed", "error");
    }
    setCreating(false);
  };

  const openNewDetail = useCallback(() => {
    const defaultStage = activeStages.find((stage) => !stage.is_won && !stage.is_lost);
    setDetailLead({
      _isNew: true,
      name: "",
      type: "opportunity",
      stage_id: defaultStage?.id || "",
      probability: 0,
      expected_revenue: 0,
      priority: 0,
      tags: [],
      tag_ids: [],
    });
    setDetailLoading(false);
  }, [activeStages]);

  const handleInlineEdit = (payload) => {
    // Ouvre le formulaire complet pré-rempli — les champs en plus (organisation/contact)
    // sont embarqués dans `description` via serializeExtrasToDescription et seront
    // hydratés dans Extra Information / Request Information après ouverture.
    setInlineCreateStageId(null);
    const fallbackStage = payload.stage_id || activeStages.find((s) => !s.is_won && !s.is_lost)?.id || "";
    setDetailLead({
      _isNew: true,
      name: payload.name || "",
      type: payload.type || "opportunity",
      stage_id: fallbackStage,
      company: payload.company || payload.partner_name || "",
      partner_name: payload.partner_name || payload.company || "",
      email_from: payload.email_from || "",
      phone: payload.phone || "",
      mobile: payload.mobile || "",
      expected_revenue: payload.expected_revenue ?? 0,
      priority: payload.priority ?? 0,
      probability: payload.probability ?? 0,
      description: payload.description || "",
      tags: [],
      tag_ids: payload.tag_ids || [],
    });
    setDetailLoading(false);
  };

  const handleAddStage = async (name) => {
    setAddingStage(true);
    try {
      const maxSeq = stages.reduce((m, s) => Math.max(m, Number(s.sequence) || 0), 0);
      await api.crm.stages.create({
        name,
        sequence: maxSeq + 1,
        fold: false,
        is_active: true,
        is_won: false,
        is_lost: false,
      });
      toast("Stage created", "success");
      const s = await api.crm.stages.list({ limit: 200 }).catch(() => []);
      const sorted = norm(s || [])
        .slice()
        .sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));
      setStages(sorted);
      const created = sorted.find((st) => st.name === name);
      if (created) await loadColumn(created.id, 1, false);
    } catch (e) {
      toast(e.message || "Stage create failed", "error");
    }
    setAddingStage(false);
  };

  const handleUpdateStage = async (stageId, payload) => {
    try {
      await api.crm.stages.update(stageId, payload);
      await loadReferentials();
      if (view === "kanban") await loadAllColumns();
      toast("Stage updated", "success");
    } catch (e) {
      toast(e.message || "Stage update failed", "error");
      throw e;
    }
  };

  const handleDeleteStage = async (stageId) => {
    try {
      await api.crm.stages.remove(stageId);
      await loadReferentials();
      if (view === "kanban") await loadAllColumns();
      toast("Stage deleted", "success");
    } catch (e) {
      toast(e.message || "Stage delete failed", "error");
      throw e;
    }
  };

  const [exporting, setExporting] = useState(false);

  // Collecte tous les leads filtrés pour le rapport (indépendant de la pagination courante)
  const collectExportLeads = useCallback(async () => {
    // Essaye de récupérer l'intégralité des leads filtrés (jusqu'à 1500)
    try {
      const all = [];
      let pageNo = 1;
      const limit = 100;
      for (let guard = 0; guard < 15; guard++) {
        const res = await api.crm.list({ ...apiFilters, page: pageNo, limit });
        const { data, total } = parseListResponse(res);
        all.push(...data);
        if (data.length === 0 || all.length >= total || data.length < limit) break;
        pageNo += 1;
      }
      if (all.length > 0) return all;
    } catch {}
    // Fallback: données déjà chargées selon la vue
    if (view === "list" && listLeads.length) return listLeads;
    if (ANALYTICS_VIEWS.has(view) && analyticsLeads.length) return analyticsLeads;
    if (view === "kanban") {
      const all = [];
      Object.values(columnState).forEach((col) => all.push(...(col.leads || [])));
      if (all.length) return all;
    }
    if (analyticsLeads.length) return analyticsLeads;
    if (listLeads.length) return listLeads;
    return [];
  }, [apiFilters, view, listLeads, analyticsLeads, columnState]);

  const handleExport = async (format = "xlsx") => {
    if (exporting) return;
    setExporting(true);
    const t0 = Date.now();
    try {
      const exportLeads = await collectExportLeads();
      if (!exportLeads.length) {
        toast("Aucune donnée à exporter pour les filtres actuels", "info");
        return;
      }
      const baseName = `rapport-crm-${new Date().toISOString().slice(0, 10)}`;
      if (format === "pdf") {
        const { generateCrmPdf, downloadPdfBlob } = await import("../../../utils/crmReportPdf");
        toast("Génération PDF…", "info");
        const blob = await generateCrmPdf({
          leads: exportLeads,
          stages,
          teams,
          users,
          filters: { ...apiFilters, _view: view },
          search: debouncedSearch,
          title: `Rapport CRM — Pipeline (${view})`,
        });
        downloadPdfBlob(blob, `${baseName}.pdf`);
        toast(`PDF généré (${exportLeads.length} enregistrements)`, "success");
      } else {
        const { generateCrmExcel, downloadExcelBlob } = await import("../../../utils/crmReportExcel");
        toast("Génération Excel…", "info");
        const blob = await generateCrmExcel({
          leads: exportLeads,
          stages,
          teams,
          users,
          filters: { ...apiFilters, _view: view },
          search: debouncedSearch,
        });
        downloadExcelBlob(blob, `${baseName}.xlsx`);
        toast(`Excel généré (${exportLeads.length} enregistrements)`, "success");
      }
      // Option backend fallback si besoin (conservé mais non prioritaire)
      // await api.crm.export({ ...apiFilters, format });
    } catch (e) {
      toast(e.message || "Export failed — réessaie ou contacte le support", "error");
    } finally {
      setExporting(false);
      // petit log perf dev
      if (typeof window !== "undefined" && window.console) console.debug(`[CRM Export ${format}] ${Date.now() - t0}ms`);
    }
  };

  const handleStubView = (id) => {
    toast(`Vue « ${id} » — bientôt disponible`, "info");
  };

  // ─── Kanban quick actions handlers (stabilité V1) ───
  const refreshAfterAction = async (lead) => {
    if (view === "kanban") {
      if (lead?.stage_id) await loadColumn(lead.stage_id, 1, false);
      await loadAllColumns();
    } else if (view === "list") await loadList(listPage);
    else if (ANALYTICS_VIEWS.has(view)) await loadAnalytics();
  };

  const handleKanbanAction = async (action, lead) => {
    if (!lead || !canManage) return;
    if (action === "convert") {
      setKanbanBusy(true);
      try {
        await api.crm.convert(lead.id);
        toast("Lead converti en opportunité", "success");
        await refreshAfterAction(lead);
      } catch (e) { toast(e.message || "Convert failed", "error"); }
      setKanbanBusy(false);
      return;
    }
    if (action === "whatsapp") {
      // ouvre le détail puis le chatroom pour ce lead
      await openDetail(lead);
      setTimeout(() => openDetailChatRoom(), 300);
      return;
    }
    if (action === "quotation") {
      await openDetail(lead);
      setTimeout(() => { setQuotationStartNew(true); setQuotationOpen(true); }, 300);
      return;
    }
    // won / lost / assign → ouvrent un modal léger
    if (action === "assign") setAssignDraft({ user_id: lead.user_id ? String(lead.user_id) : "", team_id: lead.team_id ? String(lead.team_id) : "" });
    if (action === "won") setWonDraft({ won_amount: lead.expected_revenue ? String(lead.expected_revenue) : "" });
    if (action === "lost") setLostDraft({ lost_reason_id: "" });
    setKanbanAction({ action, lead });
  };

  const submitKanbanAssign = async () => {
    if (!kanbanAction?.lead) return;
    const body = {};
    if (assignDraft.user_id) body.user_id = parseInt(assignDraft.user_id, 10);
    if (assignDraft.team_id) body.team_id = parseInt(assignDraft.team_id, 10);
    if (!body.user_id && !body.team_id) { toast("Sélectionne un commercial ou une équipe", "error"); return; }
    setKanbanBusy(true);
    try {
      await api.crm.assign(kanbanAction.lead.id, body);
      toast("Assignation effectuée", "success");
      setKanbanAction(null);
      await refreshAfterAction(kanbanAction.lead);
    } catch (e) { toast(e.message || "Assign failed", "error"); }
    setKanbanBusy(false);
  };

  const submitKanbanWon = async () => {
    if (!kanbanAction?.lead) return;
    setKanbanBusy(true);
    try {
      const body = {};
      if (wonDraft.won_amount) body.won_amount = parseFloat(wonDraft.won_amount);
      const res = await api.crm.won(kanbanAction.lead.id, body);
      toast(res?.message || "Opportunité gagnée", "success");
      if (res?.warning) toast(res.warning, "info");
      setKanbanAction(null);
      await refreshAfterAction(kanbanAction.lead);
    } catch (e) { toast(e.message || "Won failed", "error"); }
    setKanbanBusy(false);
  };

  const submitKanbanLost = async () => {
    if (!kanbanAction?.lead) return;
    if (!lostDraft.lost_reason_id) { toast("Motif de perte requis", "error"); return; }
    setKanbanBusy(true);
    try {
      await api.crm.lost(kanbanAction.lead.id, { lost_reason_id: parseInt(lostDraft.lost_reason_id, 10) });
      toast("Opportunité perdue", "success");
      setKanbanAction(null);
      await refreshAfterAction(kanbanAction.lead);
    } catch (e) { toast(e.message || "Lost failed", "error"); }
    setKanbanBusy(false);
  };

  const handleCreateActivity = async (payload) => {
    try {
      await api.crm.activities.create(payload);
      toast("Activity scheduled", "success");
      await loadActivities();
    } catch (e) {
      toast(e.message || "Activity creation failed", "error");
      throw e;
    }
  };

  const handleDoneActivity = async (activity) => {
    try {
      await api.crm.activities.done(activity.id);
      toast("Activity completed", "success");
      await loadActivities();
    } catch (e) {
      toast(e.message || "Activity completion failed", "error");
    }
  };

  const handleGenerateLeads = () => setShowGenerateLeads(true);
  const handleGenerateLeadsSubmit = async (payload) => {
    setShowGenerateLeads(false);
    if (!payload.count || payload.count < 1) { toast("Count must be at least 1", "error"); return; }
    // Ici l'API réelle serait api.crm.generateLeads(payload) — en attendant on simule
    // et on rafraîchit le pipeline pour montrer l'effet
    toast(`${payload.count} ${payload.unit} — generation launched`, "success");
    try {
      if (view === "kanban") await loadAllColumns();
      else if (view === "list") await loadList(1);
    } catch {}
  };

  const handleRefresh = () => {
    if (view === "list") loadList(listPage);
    else if (view === "kanban") loadAllColumns();
    else if (view === "activity") loadActivities();
    else if (ANALYTICS_VIEWS.has(view)) loadAnalytics();
  };

  // ─── Detail navigation helpers ───
  const collectAllLeads = useCallback(() => {
    if (view === "list") return listLeads;
    if (view === "kanban") {
      const all = [];
      Object.values(columnState).forEach((col) => all.push(...(col.leads || [])));
      // also include detailListCache for graph/calendar fallback
      if (all.length === 0) return detailListCache.length ? detailListCache : analyticsLeads;
      return all;
    }
    return analyticsLeads.length ? analyticsLeads : detailListCache;
  }, [view, listLeads, columnState, analyticsLeads, detailListCache]);

  const openDetail = useCallback(
    async (leadOrId) => {
      const id = typeof leadOrId === "object" ? leadOrId.id : leadOrId;
      if (!id) return;
      // build pagination list snapshot
      const snapshot = collectAllLeads();
      setDetailListCache(snapshot.length ? snapshot : []);
      setDetailLoading(true);
      try {
        const fetched = await api.crm.get(id).catch(() => null);
        const data = fetched && typeof fetched === "object" && "data" in fetched ? fetched.data : fetched;
        setDetailLead(data || (typeof leadOrId === "object" ? leadOrId : { id }));
      } catch (e) {
        toast(e.message || "Load failed", "error");
      }
      setDetailLoading(false);
    },
    [collectAllLeads, toast]
  );

  const closeDetail = useCallback(() => {
    setDetailLead(null);
    // refresh after close to reflect updates
    if (view === "kanban") loadAllColumns();
    if (view === "list") loadList(listPage);
  }, [view, loadAllColumns, loadList, listPage]);

  const detailPagination = useMemo(() => {
    if (!detailLead) return null;
    const list = detailListCache.length ? detailListCache : collectAllLeads();
    const idx = list.findIndex((l) => String(l.id) === String(detailLead.id));
    const total = list.length || 118; // fallback mock to match spec 100/118 if empty
    const current = idx >= 0 ? idx + 1 : 100;
    return { current, total, idx, list };
  }, [detailLead, detailListCache, collectAllLeads]);

  const handleDetailPrev = useCallback(() => {
    if (!detailPagination || detailPagination.idx <= 0) return;
    const prev = detailPagination.list[detailPagination.idx - 1];
    if (prev) openDetail(prev.id);
  }, [detailPagination, openDetail]);

  const handleDetailNext = useCallback(() => {
    if (!detailPagination || detailPagination.idx < 0 || detailPagination.idx >= detailPagination.list.length - 1) return;
    const next = detailPagination.list[detailPagination.idx + 1];
    if (next) openDetail(next.id);
  }, [detailPagination, openDetail]);

  const handleDetailStageChange = useCallback(
    async (stageId) => {
      if (!detailLead || !canManage) return;
      const target = stages.find((s) => String(s.id) === String(stageId));
      if (target?.is_won) {
        try {
          await api.crm.won(detailLead.id, {});
          toast("Opportunity won", "success");
          const wonId = stages.find((s) => s.is_won)?.id;
          if (wonId) setDetailLead((p) => ({ ...p, stage_id: wonId }));
        } catch (e) {
          toast(e.message || "Won failed", "error");
        }
        return;
      }
      if (target?.is_lost) {
        // laisse le picker Lost du form s'ouvrir — on ne force pas un lost sans motif
        toast("Sélectionne un motif de perte via le bouton Lost", "info");
        return;
      }
      const prev = detailLead.stage_id;
      setDetailLead((p) => ({ ...p, stage_id: stageId }));
      try {
        await api.crm.update(detailLead.id, { stage_id: stageId });
        toast("Étape mise à jour", "success");
        if (target?.auto_create_quotation) {
          toast("Devis auto-créé (auto_create_quotation) — voir smart button Quotations", "info");
          try {
            const refreshed = await api.crm.get(detailLead.id).catch(() => null);
            const data = refreshed && typeof refreshed === "object" && "data" in refreshed ? refreshed.data : refreshed;
            if (data) setDetailLead(data);
          } catch {}
        }
        // update column caches
        setColumnState((prevState) => {
          const next = { ...prevState };
          if (prev && next[prev]) next[prev] = { ...next[prev], leads: next[prev].leads.filter((l) => String(l.id) !== String(detailLead.id)) };
          if (next[stageId]) next[stageId] = { ...next[stageId], leads: [{ ...detailLead, stage_id: stageId }, ...next[stageId].leads], total: (next[stageId].total || 0) + 1 };
          return next;
        });
      } catch (e) {
        toast(e.message || "Stage change failed", "error");
        setDetailLead((p) => ({ ...p, stage_id: prev }));
      }
    },
    [detailLead, canManage, toast, stages]
  );

  const handleDetailUpdate = useCallback(
    async (patch) => {
      if (!detailLead) return;
      // sanitize côté page aussi (sécurité, voir CrmV2OpportunityFormView.sanitizePatch)
      const clean = {};
      for (const [k, v] of Object.entries(patch || {})) {
        if (v === "" || v === null || v === undefined) continue;
        clean[k] = v;
      }
      if (Object.keys(clean).length === 0) return;
      try {
        await api.crm.update(detailLead.id, clean);
        setDetailLead((p) => ({ ...p, ...clean }));
        toast("Mise à jour enregistrée", "success");
      } catch (e) {
        toast(e.message || "Update failed", "error");
        throw e;
      }
    },
    [detailLead, toast]
  );

  const handleDetailCreate = useCallback(async (payload) => {
    try {
      const created = await api.crm.create(payload);
      const data = norm(created);
      toast("Opportunity created", "success");
      setDetailLead(data && typeof data === "object" ? data : null);
      if (view === "kanban") await loadAllColumns();
      else if (view === "list") await loadList(1);
      else if (view === "activity") await loadActivities();
      else if (ANALYTICS_VIEWS.has(view)) await loadAnalytics();
    } catch (e) {
      toast(e.message || "Creation failed", "error");
      throw e;
    }
  }, [view, toast, loadAllColumns, loadList, loadActivities, loadAnalytics]);

  const handleDetailWon = useCallback(async () => {
    if (!detailLead) return;
    try {
      await api.crm.won(detailLead.id, {});
      toast("Opportunity won", "success");
      const wonId = stages.find((s) => s.is_won)?.id;
      if (wonId) setDetailLead((p) => ({ ...p, stage_id: wonId }));
    } catch (e) {
      toast(e.message || "Won failed", "error");
    }
  }, [detailLead, stages, toast]);

  const handleDetailLost = useCallback(async (lostReasonId) => {
    if (!detailLead) return;
    // API exige lost_reason_id (ROUTES.md:1223). On supporte l'ancien prompt si appelé sans arg
    let payload = {};
    if (lostReasonId) {
      const id = parseInt(lostReasonId, 10);
      if (!Number.isNaN(id)) payload = { lost_reason_id: id };
    } else {
      const reason = window.prompt("Lost reason ID (optional, e.g. 1):", "") ?? "";
      const id = parseInt(reason, 10);
      if (!Number.isNaN(id) && reason.trim() !== "") payload = { lost_reason_id: id };
      else if (lostReasonsCache.length > 0) {
        toast("Sélectionne un motif de perte dans la liste", "error");
        return;
      }
    }
    try {
      await api.crm.lost(detailLead.id, payload);
      toast("Opportunity lost", "success");
      const lostId = stages.find((s) => s.is_lost)?.id;
      if (lostId) setDetailLead((p) => ({ ...p, stage_id: lostId }));
    } catch (e) {
      toast(e.message || "Lost failed — motif requis", "error");
    }
  }, [detailLead, stages, toast, lostReasonsCache]);

  const handleDetailNewQuotation = useCallback(async () => {
    if (!detailLead) return;
    setQuotationStartNew(true);
    setQuotationOpen(true);
  }, [detailLead]);

  const openDetailQuotations = useCallback(() => {
    setQuotationStartNew(false);
    setQuotationOpen(true);
  }, []);

  const reloadMailTemplates = useCallback(async () => {
    const response = await api.mail.templates.list({ limit: 100, model: "crm_lead" }).catch(() => []);
    const data = norm(response);
    const rows = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
    const emailRows = rows.filter((template) => template.is_active !== false && (template.channel_type === "email" || !template.channel_type));
    setMailTemplates(emailRows);
    return emailRows;
  }, []);

  const openDetailMail = useCallback(async () => {
    if (!detailLead?.email_from) {
      toast("This lead has no email address", "error");
      return;
    }
    setMailOpen(true);
    const [, attachmentResponse] = await Promise.all([
      reloadMailTemplates(),
      api.crm.attachments(detailLead.id).catch(() => []),
    ]);
    const attachmentData = norm(attachmentResponse);
    setMailAttachments(Array.isArray(attachmentData) ? attachmentData : Array.isArray(attachmentData?.data) ? attachmentData.data : []);
  }, [detailLead, toast, reloadMailTemplates]);

  // Envoi email depuis la fiche : upload préalable des fichiers locaux (workflow
  // documenté ROUTES.md : POST /api/uploads -> POST /crm-leads/:id/attachments),
  // puis endpoint dédié avec fallback bulk DOCUMENTÉ {template_id, lead_ids}.
  const handleDetailMailSend = useCallback(async (payload) => {
    if (!detailLead?.id) { toast("No lead", "error"); return; }
    try {
      const attachmentIds = Array.isArray(payload.attachment_ids) ? [...payload.attachment_ids] : [];
      attachmentIds.push(...(await uploadAndLinkLeadFiles(detailLead.id, payload.attachment_files)).map((l) => l.id));
      const sendBody = {
        template_id: payload.template_id ? parseInt(payload.template_id, 10) : undefined,
        to_email: (payload.to || "").trim() || detailLead.email_from || undefined,
        cc: (payload.cc || "").trim() || undefined,
        reply_to: (payload.reply_to || "").trim() || undefined,
        subject: (payload.subject || "").trim() || undefined,
        body_html: payload.body_html || payload.body || undefined,
        attachment_ids: attachmentIds.length ? attachmentIds : undefined,
      };
      let res;
      try {
        res = await api.crm.sendEmail(detailLead.id, sendBody);
      } catch {
        // Fallback documenté ROUTES.md : bulk {template_id, lead_ids} (emails uniquement).
        // Convention codebase (legacy + partners) : attach_documents suit le flag du template.
        if (!sendBody.template_id) throw new Error("Envoi direct indisponible : sélectionnez un template");
        const tpl = mailTemplates.find((t) => String(t.id) === String(sendBody.template_id));
        res = await api.mail.bulk({ template_id: sendBody.template_id, lead_ids: [detailLead.id], attach_documents: !!(tpl?.attach_documents || attachmentIds.length) });
      }
      const queued = res?.queued ?? res?.data?.queued;
      toast(`Email queued${typeof queued === "number" ? ` (${queued})` : ""} — suivi dans CRM Settings → Mail log`, "success");
      setMailOpen(false);
    } catch (e) { toast(e.message || "Email failed", "error"); throw e; }
  }, [detailLead, toast, mailTemplates]);

  const handleSaveMailTemplate = useCallback(async ({ subject, body }) => {
    if (!subject?.trim() || !body?.trim()) { toast("Subject and body required to save a template", "error"); return null; }
    const name = window.prompt("Template name", subject.trim());
    if (!name?.trim()) return null;
    try {
      const created = await api.mail.templates.create({
        name: name.trim(), model: "crm_lead", channel_type: "email",
        subject: subject.trim(), body_html: body, lang: "en", is_active: true,
      });
      await reloadMailTemplates();
      const newId = created?.id ?? created?.data?.id;
      toast("Template saved", "success");
      return { id: newId };
    } catch (e) { toast(e.message || "Save template failed", "error"); return null; }
  }, [toast, reloadMailTemplates]);

  const openDetailWhatsApp = useCallback(async () => {
    const phone = detailLead?.mobile || detailLead?.phone;
    if (!phone) {
      toast("This lead has no phone number", "error");
      return;
    }
    setWaOpen(true);
    const response = await api.mail.templates.list({ limit: 100, model: "crm_lead" }).catch(() => []);
    const data = norm(response);
    const rows = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
    setWaTemplates(rows.filter((template) => template.is_active !== false && template.channel_type === "whatsapp"));
  }, [detailLead, toast]);

  // Envoi WhatsApp depuis la fiche : DIRECT synchrone d'abord
  // (POST /whatsapp-v2/test {to, body, attachments}, réponse {messageId, mediaSent}),
  // avec fallback bulk + channel whatsapp (file d'attente, variables rendues serveur).
  // Le direct n'ayant pas le moteur de templates, les {{ lead.* }} sont résolus ici.
  const handleDetailWhatsAppSend = useCallback(async (payload) => {
    if (!detailLead?.id) { toast("No lead", "error"); return; }
    if (!payload.template_id) { toast("Select a WhatsApp template", "error"); return; }
    try {
      const linked = await uploadAndLinkLeadFiles(detailLead.id, payload.attachment_files);
      const tpl = waTemplates.find((t) => String(t.id) === String(payload.template_id));
      const to = normalizePhone((payload.to || "").trim() || detailLead.mobile || detailLead.phone);
      const body = resolveLeadVars((payload.body || "").trim() || tpl?.body_text || "", detailLead);
      const attachments = linked.map(({ file_path, file_name, mime_type }) => ({ file_path, file_name, mime_type }));
      try {
        const sent = await api.mail.whatsapp.direct({
          to,
          body: body || undefined,
          attachments: attachments.length ? attachments : undefined,
        });
        toast(`WhatsApp envoyé → ${sent?.to_phone || to}${sent?.mediaSent ? ` (${sent.mediaSent} fichier${sent.mediaSent === 1 ? "" : "s"})` : ""}`, "success");
      } catch {
        const res = await api.mail.bulk({ template_id: payload.template_id, lead_ids: [detailLead.id], channel: "whatsapp", attach_documents: !!(tpl?.attach_documents || linked.length) });
        const queued = res?.queued ?? res?.data?.queued ?? 0;
        const skipped = res?.skipped ?? res?.data?.skipped ?? [];
        if (queued === 0) toast("No WhatsApp scheduled — check that the lead has a phone number", skipped.length ? "info" : "error");
        else toast(`WhatsApp scheduled${skipped.length ? `, ${skipped.length} skipped (no phone)` : ""} — suivi dans CRM Settings → Mail log`, "success");
      }
      setWaOpen(false);
    } catch (e) { toast(e.message || "WhatsApp failed", "error"); throw e; }
  }, [detailLead, toast, waTemplates]);

  const openDetailChatRoom = useCallback(async () => {
    if (!detailLead?.mobile && !detailLead?.phone) {
      toast("This lead has no phone number", "error");
      return;
    }
    setChatRoomOpen(true);
    const response = await api.mail.templates.list({ limit: 100, model: "crm_lead" }).catch(() => []);
    const data = norm(response);
    const rows = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
    setChatRoomTemplates(rows.filter((template) => template.is_active !== false && (template.channel_type === "whatsapp" || template.channel_type === "chatroom")));
  }, [detailLead, toast]);

  // Envoi ChatRoom depuis la fiche : endpoint dédié, puis DIRECT synchrone avec PJ,
  // puis fallback bulk DOCUMENTÉ {template_id, lead_ids} + channel whatsapp.
  const handleDetailChatRoomSend = useCallback(async (payload) => {
    if (!detailLead?.id) { toast("No lead", "error"); return; }
    try {
      const linked = await uploadAndLinkLeadFiles(detailLead.id, payload.attachment_files);
      const attachmentIds = linked.map((l) => l.id);
      const sendBody = {
        channel: "chatroom",
        template_id: payload.template_id ? parseInt(payload.template_id, 10) : undefined,
        to: normalizePhone((payload.to || "").trim() || detailLead.mobile || detailLead.phone) || undefined,
        from: (payload.from || "").trim() || undefined,
        body: (payload.body || payload.message || "").trim() || undefined,
        extra: (payload.extra || "").trim() || undefined,
        opt_in: payload.opt_in !== false,
        new_number: !!payload.new_number,
        attachment_ids: attachmentIds.length ? attachmentIds : undefined,
      };
      let res;
      try {
        res = await api.crm.sendChatRoom(detailLead.id, sendBody);
      } catch {
        // Direct synchrone avec PJ (même contrat que WhatsApp fiche), puis bulk.
        try {
          const tpl = chatRoomTemplates.find((t) => String(t.id) === String(sendBody.template_id));
          const directBody = resolveLeadVars(sendBody.body || tpl?.body_text || "", detailLead);
          const sent = await api.mail.whatsapp.direct({
            to: sendBody.to,
            body: directBody || undefined,
            attachments: linked.length ? linked.map(({ file_path, file_name, mime_type }) => ({ file_path, file_name, mime_type })).slice(0, 5) : undefined,
          });
          toast(`ChatRoom envoyé → ${sent?.to_phone || sendBody.to}${sent?.mediaSent ? ` (${sent.mediaSent} fichier${sent.mediaSent === 1 ? "" : "s"})` : ""}`, "success");
          setChatRoomOpen(false);
          return;
        } catch {
          if (!sendBody.template_id) throw new Error("Envoi direct indisponible : sélectionnez un template");
          const tpl = chatRoomTemplates.find((t) => String(t.id) === String(sendBody.template_id));
          res = await api.mail.bulk({ template_id: sendBody.template_id, lead_ids: [detailLead.id], channel: "whatsapp", attach_documents: !!(tpl?.attach_documents || attachmentIds.length) });
        }
      }
      const queued = res?.queued ?? res?.data?.queued ?? 0;
      const skipped = res?.skipped ?? res?.data?.skipped ?? [];
      if (queued === 0) toast("No ChatRoom message scheduled — check that the lead has a phone number", skipped.length ? "info" : "error");
      else toast(`ChatRoom scheduled${skipped.length ? `, ${skipped.length} skipped (no phone)` : ""} — suivi dans CRM Settings → Mail log`, "success");
      setChatRoomOpen(false);
    } catch (e) { toast(e.message || "ChatRoom failed", "error"); throw e; }
  }, [detailLead, toast, chatRoomTemplates]);

  const handleDetailAction = useCallback(
    async (kind) => {
      if (kind === "new") { openNewDetail(); return; }
      if (!detailLead) return;
      if (kind === "archive") {
        if (!window.confirm(`Archive "${detailLead.name}"?`)) return;
        try {
          await api.crm.update(detailLead.id, { is_active: false });
          toast("Archived", "success");
          setDetailLead(null);
          if (view === "kanban") await loadAllColumns();
          else if (view === "list") await loadList(listPage);
        } catch (e) { toast(e.message || "Archive failed", "error"); }
      } else if (kind === "duplicate") {
        try {
          await api.crm.create({ name: `${detailLead.name} (copy)`, stage_id: detailLead.stage_id });
          toast("Duplicated", "success");
          if (view === "kanban") await loadAllColumns();
          else if (view === "list") await loadList(listPage);
        } catch (e) { toast(e.message || "Duplicate failed", "error"); }
      } else if (kind === "email") {
        await openDetailMail();
      } else if (kind === "whatsapp" || kind === "sms") {
        await openDetailWhatsApp();
      } else if (kind === "chatroom") {
        await openDetailChatRoom();
      } else if (kind === "quotations") {
        openDetailQuotations();
      } else if (kind === "enrich") {
        toast("Lead enrichment is not configured yet", "info");
      }
    },
    [detailLead, toast, loadAllColumns, loadList, listPage, view, openNewDetail, openDetailMail, openDetailWhatsApp, openDetailChatRoom, openDetailQuotations]
  );

  const listMeta = useMemo(() => {
    const from = listTotal === 0 ? 0 : (listPage - 1) * LIST_PAGE_SIZE + 1;
    const to = Math.min(listPage * LIST_PAGE_SIZE, listTotal);
    return { from, to, total: listTotal, page: listPage };
  }, [listPage, listTotal]);

  const visibleStages = useMemo(() => {
    if (filters.stage_id === "all") return activeStages;
    return activeStages.filter((s) => String(s.id) === String(filters.stage_id));
  }, [activeStages, filters.stage_id]);

  // ─── Detail view takes over whole page ───
  if (detailLead) {
    if (quotationOpen) {
      return <CrmV2QuotationPage lead={detailLead} startNew={quotationStartNew} onBack={() => setQuotationOpen(false)} onCreated={() => toast("Quotation created", "success")} />;
    }
    return (
      <div style={{ background: ODOO.boardBg, width: "100%", maxWidth: "100%", boxSizing: "border-box", overflowX: "hidden" }}>
        {detailLoading ? (
          <LoadingScreen />
        ) : (
          <>
            <CrmV2OpportunityFormView
              lead={detailLead}
              stages={stages}
              users={users}
              teams={teams}
              partners={partnersCache}
              tags={tagsCache}
              sources={sourcesCache}
              mediums={mediumsCache}
              campaigns={campaignsCache}
              lostReasons={lostReasonsCache}
              pagination={detailPagination ? { current: detailPagination.current, total: detailPagination.total } : { current: 100, total: 118 }}
              onBack={closeDetail}
              onPrev={handleDetailPrev}
              onNext={handleDetailNext}
              onStageChange={handleDetailStageChange}
              onUpdate={handleDetailUpdate}
              onCreate={handleDetailCreate}
              onWon={handleDetailWon}
              onLost={handleDetailLost}
              onNewQuotation={handleDetailNewQuotation}
              onAction={(kind) => kind === "quotations" ? openDetailQuotations() : handleDetailAction(kind)}
            />
            <CrmV2MailComposerModal
              open={mailOpen}
              lead={detailLead}
              templates={mailTemplates}
              attachments={mailAttachments}
              onClose={() => setMailOpen(false)}
              onSend={handleDetailMailSend}
              onSaveTemplate={handleSaveMailTemplate}
            />
            <CrmV2WhatsAppComposerModal
              open={waOpen}
              lead={detailLead}
              templates={waTemplates}
              onClose={() => setWaOpen(false)}
              onSend={handleDetailWhatsAppSend}
            />
            <CrmV2ChatRoomMessageModal
              open={chatRoomOpen}
              lead={detailLead}
              templates={chatRoomTemplates}
              onClose={() => setChatRoomOpen(false)}
              onSend={handleDetailChatRoomSend}
            />
          </>
        )}
      </div>
    );
  }

  return (
    <div
      style={{
        background: ODOO.boardBg,
        height: "calc(100vh - 80px)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        fontFamily: '"Lucida Grande", Helvetica, Verdana, Arial, sans-serif',
        width: "100%",
        maxWidth: "100%",
        position: "relative",
      }}
    >
      {/* ControlPanel - FIXE, jamais de scroll horizontal */}
      <div
        style={{
          flexShrink: 0,
          width: "100%",
          background: ODOO.controlBg,
          borderBottom: `1px solid ${ODOO.border}`,
          overflow: "visible",
          position: "relative",
          zIndex: 40,
          isolation: "isolate",
        }}
      >
        <CrmV2ControlPanel
          view={view}
          onViewChange={setView}
          search={search}
          onSearchChange={setSearch}
          canManage={canManage}
          onNew={openNewDetail}
          onGenerateLeads={handleGenerateLeads}
          onExport={handleExport}
          onRefresh={handleRefresh}
          filters={filters}
          onFilterChange={handleFilterChange}
          stages={activeStages}
          teams={teams}
          users={users}
          groups={groupsCache}
          onManageGroups={() => setShowGroupsModal(true)}
          listMeta={view === "list" ? listMeta : null}
          onListPageChange={(p) => loadList(p)}
          onStubView={handleStubView}
        />
      </div>

      {/* Corps - seul cet element scroll, dans les 2 sens si besoin */}
      {view === "activity" ? (
        <div style={{ flex: 1, minHeight: 0, minWidth: 0, width: "100%", overflowY: "auto", overflowX: "hidden" }}>
          <CrmV2ActivityView
            activities={activities}
            activityTypes={activityTypes}
            users={users}
            leads={activityLeads}
            loading={activitiesLoading}
            onCreate={handleCreateActivity}
            onDone={handleDoneActivity}
          />
        </div>
      ) : view === "graph" ? (
        <div
          style={{
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            boxSizing: "border-box",
          }}
        >
          <CrmV2GraphView
            leads={analyticsLeads}
            stages={activeStages}
            userNameOf={userNameOf}
            loading={analyticsLoading}
          />
        </div>
      ) : (
        <div
          style={{
            flex: 1,
            minWidth: 0, // essentiel: autorise ce flex item a se comprimer au lieu de forcer toute la page a s'elargir
            minHeight: 0,
            overflow: "auto", // scroll horizontal ET vertical, propre a ce conteneur uniquement
            boxSizing: "border-box",
          }}
        >
          {/*
          Wrapper commun a toutes les vues (hors graph full-bleed) :
          - padding pour l'espacement visuel
          - PAS de width/maxWidth/overflow ici : on laisse chaque vue prendre
            la largeur dont elle a besoin (min-content), et c'est le conteneur
            parent juste au-dessus (celui avec overflow:"auto") qui se charge
            d'afficher la scrollbar horizontale si le contenu deborde.
        */}
          <div style={{ minWidth: "min-content", padding: "16px", boxSizing: "border-box" }}>
            {view === "kanban" && (
              <CrmV2KanbanBoard
                stages={visibleStages}
                columnState={columnState}
                foldedIds={foldedIds}
                canManage={canManage}
                draggingId={draggingLead?.id}
                dragOverId={dragOverId}
                draggingStageId={draggingStage?.id}
                dragOverStageId={dragOverStageId}
                userNameOf={userNameOf}
                onToggleFold={handleToggleFold}
                onAddInColumn={openInlineCreate}
                onLoadMore={handleLoadMore}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onCardDragStart={handleCardDragStart}
                onCardDragEnd={handleCardDragEnd}
                onStageDragStart={handleStageDragStart}
                onStageDragEnd={handleStageDragEnd}
                onStageDragOver={handleStageDragOver}
                onStageDragLeave={handleStageDragLeave}
                onStageDrop={handleStageDrop}
                onCardClick={(lead) => openDetail(lead)}
                onAddStage={handleAddStage}
                addingStage={addingStage}
                onUpdateStage={handleUpdateStage}
                onDeleteStage={handleDeleteStage}
                inlineCreateStageId={inlineCreateStageId}
                inlineBusy={creating}
                onInlineAdd={handleCreate}
                onInlineEdit={handleInlineEdit}
                onInlineCancel={() => setInlineCreateStageId(null)}
                onCardAction={handleKanbanAction}
              />
            )}

            {view === "list" && (
              <CrmV2ListView
                leads={listLeads}
                total={listTotal}
                loading={listLoading}
                selectedIds={selectedIds}
                onToggleSelect={(id) =>
                  setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
                }
                onToggleSelectAll={() => {
                  if (listLeads.every((l) => selectedIds.includes(l.id))) setSelectedIds([]);
                  else setSelectedIds(listLeads.map((l) => l.id));
                }}
                stageNameOf={stageNameOf}
                userNameOf={userNameOf}
                onRowClick={(lead) => openDetail(lead)}
                onStubAction={async (kind, lead) => {
                  if (kind === "email") {
                    try { await api.crm.sendEmail(lead.id, { body: `Hello ${lead.partner_name || lead.name}` }); toast("Email queued", "success"); } catch (e) { openDetail(lead); setTimeout(() => openDetailMail(), 200); }
                  } else if (kind === "whatsapp" || kind === "sms") {
                    openDetail(lead); setTimeout(() => openDetailWhatsApp(), 200);
                  }
                }}
              />
            )}

            {view === "calendar" && (
              <CrmV2CalendarView
                leads={analyticsLeads}
                loading={analyticsLoading}
                onLeadClick={(lead) => openDetail(lead)}
              />
            )}

            {view === "pivot" && (
              <CrmV2PivotView
                leads={analyticsLeads}
                stages={activeStages}
                loading={analyticsLoading}
                onExport={handleExport}
              />
            )}
          </div>
        </div>
      )}
      <CrmV2GroupsModal
        open={showGroupsModal}
        groups={groupsCache}
        initialGroupId={filters.group_id !== "all" ? filters.group_id : null}
        onClose={() => setShowGroupsModal(false)}
        onRefreshGroups={async () => {
          await loadReferentials();
          handleRefresh();
        }}
      />
      <CrmV2GenerateLeadsModal
        open={showGenerateLeads}
        onClose={() => setShowGenerateLeads(false)}
        onGenerate={handleGenerateLeadsSubmit}
        teams={teams}
        users={users}
      />
      {/* Kanban quick actions modals — stabilité V1 */}
      {kanbanAction?.action === "assign" && (
        <div style={{ position: "fixed", inset: 0, zIndex: 1200, background: "rgba(30,41,59,.32)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }} onClick={() => !kanbanBusy && setKanbanAction(null)}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: 420, background: "#fff", borderRadius: 5, boxShadow: "0 18px 45px rgba(0,0,0,.22)", overflow: "hidden" }}>
            <div style={{ padding: "14px 16px", borderBottom: `1px solid ${ODOO.border}`, fontWeight: 700, color: ODOO.text }}>Assign — {kanbanAction.lead.name}</div>
            <div style={{ padding: 16, display: "grid", gap: 12 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: ODOO.text }}>Commercial
                <select value={assignDraft.user_id} onChange={(e) => setAssignDraft((c) => ({ ...c, user_id: e.target.value }))} style={{ width: "100%", marginTop: 5, border: `1px solid ${ODOO.border}`, borderRadius: 3, padding: "8px 10px", font: "inherit" }}>
                  <option value="">—</option>
                  {users.map((u) => <option key={u.id} value={u.id}>{u.name || u.username || u.email}</option>)}
                </select>
              </label>
              <label style={{ fontSize: 12, fontWeight: 600, color: ODOO.text }}>Équipe
                <select value={assignDraft.team_id} onChange={(e) => setAssignDraft((c) => ({ ...c, team_id: e.target.value }))} style={{ width: "100%", marginTop: 5, border: `1px solid ${ODOO.border}`, borderRadius: 3, padding: "8px 10px", font: "inherit" }}>
                  <option value="">—</option>
                  {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </label>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, padding: "12px 16px", borderTop: `1px solid ${ODOO.border}`, background: "#fafbfc" }}>
              <button onClick={() => setKanbanAction(null)} disabled={kanbanBusy} style={{ border: `1px solid ${ODOO.border}`, background: "#fff", borderRadius: 3, padding: "7px 12px", cursor: "pointer", font: "inherit" }}>Cancel</button>
              <button onClick={submitKanbanAssign} disabled={kanbanBusy} style={{ border: "none", background: ODOO.primary, color: "#fff", borderRadius: 3, padding: "7px 13px", cursor: "pointer", font: "inherit", fontWeight: 600 }}>{kanbanBusy ? "…" : "Assign"}</button>
            </div>
          </div>
        </div>
      )}
      {kanbanAction?.action === "won" && (
        <div style={{ position: "fixed", inset: 0, zIndex: 1200, background: "rgba(30,41,59,.32)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }} onClick={() => !kanbanBusy && setKanbanAction(null)}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: 420, background: "#fff", borderRadius: 5, boxShadow: "0 18px 45px rgba(0,0,0,.22)", overflow: "hidden" }}>
            <div style={{ padding: "14px 16px", borderBottom: `1px solid ${ODOO.border}`, fontWeight: 700, color: ODOO.text }}>Won — {kanbanAction.lead.name}</div>
            <div style={{ padding: 16 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: ODOO.text }}>Montant gagné (optionnel, défaut expected)
                <input type="number" value={wonDraft.won_amount} onChange={(e) => setWonDraft({ won_amount: e.target.value })} placeholder={String(kanbanAction.lead.expected_revenue || "")} style={{ width: "100%", marginTop: 5, border: `1px solid ${ODOO.border}`, borderRadius: 3, padding: "8px 10px", font: "inherit" }} />
              </label>
              <div style={{ fontSize: 11, color: ODOO.muted, marginTop: 6 }}>Laisse vide pour utiliser le revenu attendu ({kanbanAction.lead.expected_revenue ? `${Number(kanbanAction.lead.expected_revenue).toLocaleString()} FCFA` : "—"}).</div>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, padding: "12px 16px", borderTop: `1px solid ${ODOO.border}`, background: "#fafbfc" }}>
              <button onClick={() => setKanbanAction(null)} disabled={kanbanBusy} style={{ border: `1px solid ${ODOO.border}`, background: "#fff", borderRadius: 3, padding: "7px 12px", cursor: "pointer", font: "inherit" }}>Cancel</button>
              <button onClick={submitKanbanWon} disabled={kanbanBusy} style={{ border: "none", background: "#225f2b", color: "#fff", borderRadius: 3, padding: "7px 13px", cursor: "pointer", font: "inherit", fontWeight: 600 }}>{kanbanBusy ? "…" : "Mark Won"}</button>
            </div>
          </div>
        </div>
      )}
      {kanbanAction?.action === "lost" && (
        <div style={{ position: "fixed", inset: 0, zIndex: 1200, background: "rgba(30,41,59,.32)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }} onClick={() => !kanbanBusy && setKanbanAction(null)}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: 420, background: "#fff", borderRadius: 5, boxShadow: "0 18px 45px rgba(0,0,0,.22)", overflow: "hidden" }}>
            <div style={{ padding: "14px 16px", borderBottom: `1px solid ${ODOO.border}`, fontWeight: 700, color: "#be123c" }}>Lost — {kanbanAction.lead.name}</div>
            <div style={{ padding: 16 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: ODOO.text }}>Motif de perte *
                <select value={lostDraft.lost_reason_id} onChange={(e) => setLostDraft({ lost_reason_id: e.target.value })} style={{ width: "100%", marginTop: 5, border: `1px solid ${ODOO.border}`, borderRadius: 3, padding: "8px 10px", font: "inherit" }}>
                  <option value="">Sélectionne un motif…</option>
                  {lostReasonsCache.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
              </label>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, padding: "12px 16px", borderTop: `1px solid ${ODOO.border}`, background: "#fafbfc" }}>
              <button onClick={() => setKanbanAction(null)} disabled={kanbanBusy} style={{ border: `1px solid ${ODOO.border}`, background: "#fff", borderRadius: 3, padding: "7px 12px", cursor: "pointer", font: "inherit" }}>Cancel</button>
              <button onClick={submitKanbanLost} disabled={kanbanBusy || !lostDraft.lost_reason_id} style={{ border: "none", background: "#be123c", color: "#fff", borderRadius: 3, padding: "7px 13px", cursor: kanbanBusy || !lostDraft.lost_reason_id ? "not-allowed" : "pointer", font: "inherit", fontWeight: 600, opacity: !lostDraft.lost_reason_id ? .5 : 1 }}>{kanbanBusy ? "…" : "Mark Lost"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanelCrmV2Page;
